import { cn } from '../lib/cn.js'

/**
 * The icon set. One set, no library.
 *
 * Every glyph is a single stroked path on a 24x24 viewBox at stroke weight
 * 1.75 with round caps, drawn in `currentColor`. There is no icon font and no
 * CDN dependency: the set is not Lucide, Heroicons or Feather, and nothing is
 * substituted from one. If a glyph is needed that is not here, add it here
 * rather than drawing one inline.
 *
 * `strokeWidth` is the one dial, and only for glyphs drawn smaller than 16px.
 */

export const ICON_PATHS = {
  today: 'M3 12h4l3 8 4-16 3 8h4',
  jobs: 'M4 7h16M4 12h16M4 17h10',
  schedule: 'M4 5h16v15H4zM4 9h16M9 3v4M15 3v4',
  inbox: 'M3 8l9 6 9-6M3 8v10h18V8l-9-5z',
  clients: 'M16 19v-2a4 4 0 00-8 0v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  quotes: 'M8 6h8M8 10h8M8 14h5M6 3h12v18l-3-2-3 2-3-2-3 2z',
  invoices: 'M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6',
  catalog: 'M20 7l-8-4-8 4 8 4 8-4zM4 12l8 4 8-4M4 17l8 4 8-4',
  people: 'M17 20h5v-2a3 3 0 00-3-3M9 20H4v-2a5 5 0 015-5h2a5 5 0 015 5v2M12 7a3 3 0 100-6 3 3 0 000 6z',
  // A six-tooth cog, one closed body plus a hub. The previous glyph was a
  // hub and six radiating strokes with no body, so at 20px it read as a sun.
  settings:
    'M8.48 5.89L9.74 2.36L14.26 2.36L15.53 5.89L19.22 5.22L21.48 9.14L19.05 12.00L21.48 14.86L19.22 18.78L15.53 18.11L14.26 21.64L9.74 21.64L8.48 18.11L4.78 18.78L2.52 14.86L4.95 12.00L2.52 9.14L4.78 5.22ZM12 15a3 3 0 100-6 3 3 0 000 6z',
  more: 'M5 12h.01M12 12h.01M19 12h.01',

  /* Added for the scheduling module. Same construction as the rest. */
  back: 'M15 6l-6 6 6 6',
  forward: 'M9 6l6 6-6 6',
  close: 'M6 6l12 12M18 6L6 18',
  // A box with an arrow leaving its corner: open somewhere else.
  external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5',
  map: 'M9 4l6 2 6-2v14l-6 2-6-2-6 2V6z M9 4v14 M15 6v14',
  columns: 'M4 5h16v14H4z M10 5v14 M16 5v14',
  grid: 'M4 5h16v14H4z M4 10h16 M4 15h16 M12 5v14',
  timeline: 'M4 6h16M4 12h16M4 18h10M8 4v4M14 10v4M6 16v4',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z M12 15a3 3 0 100-6 3 3 0 000 6z',
  // The same lens as `eye` with a slash through it, and no pupil: these two are
  // drawn at 12px on the board headers, where a third stroke inside the lens
  // turns into a smudge.
  eyeOff: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z M4.5 19.5L19.5 4.5',
  zoomIn: 'M10 4a6 6 0 100 12 6 6 0 000-12zM20 20l-4.2-4.2M10 7v6M7 10h6',
  zoomOut: 'M10 4a6 6 0 100 12 6 6 0 000-12zM20 20l-4.2-4.2M7 10h6',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13M10 11v6M14 11v6',

  /* Added for the list toolbars. */
  search: 'M10 4a6 6 0 100 12 6 6 0 000-12zM20 20l-4.2-4.2',
  filter: 'M4 5h16l-6 7v7l-4-2v-5z',
  sort: 'M7 4v16M4 7l3-3 3 3M17 20V4M14 17l3 3 3-3',
  group: 'M4 6h16M4 6v12M9 12h11M9 18h11',
  // Two columns of dots, the universal "pick this up". Drawn as six one-pixel
  // strokes rather than circles so it stays crisp at 16px like the rest of the
  // set, which is all strokes on one path.
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  chevronDown: 'M6 9l6 6 6-6',
  check: 'M5 13l4 4L19 7',
  edit: 'M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4',
  phone: 'M6.5 3.5h3l1.5 4-2.5 1.5a12 12 0 006 6l1.5-2.5 4 1.5v3A2 2 0 0018 19 16 16 0 015 6a2 2 0 011.5-2.5z',
  mail: 'M4 6h16v12H4zM4 6l8 6 8-6',
  paperclip: 'M16.5 7.5V15a4.5 4.5 0 01-9 0V6.5a3 3 0 016 0V15a1.5 1.5 0 01-3 0V8',

  /* The office task board, on the rail. Three columns of differing height,
     which is a board with different numbers of cards in it and not a bar
     chart at 20px — the frame a `columns` glyph puts round them reads as a
     window at rail size. */
  board: 'M4 4h4v10H4z M10 4h4v15h-4z M16 4h4v7h-4z',

  /* Added for the ServiceM8 freshness chip on the board. */
  // Drawn as three quarters of a circle with an arrowhead, so it reads as
  // turning when it is spun and as "refresh" when it is not.
  refresh: 'M20 12a8 8 0 11-2.34-5.66M20 4v4h-4',
  alert: 'M12 4l9 16H3zM12 10v4M12 17h.01',

  /* Added for the services on a job. One glyph per `ServiceIcon`.
   *
   * These are what make a service row readable at arm's length in a subfloor:
   * the name is 14px and the tile is 42, so the glyph is what a technician
   * actually recognises. Drawn as one `d` each like the rest of the set, which
   * is why the round bodies are cubics rather than an `<ellipse>`. */
  termite:
    'M12 3c-3 3-3 6-1 8-3 0-5 2-5 5a6 6 0 0012 0c0-3-2-5-5-5 2-2 2-5-1-8z M12 21v-6 M7 15l-3 1 M17 15l3 1',
  roach:
    'M12 6c2.8 0 5 3.1 5 7s-2.2 7-5 7-5-3.1-5-7 2.2-7 5-7z M9 6L6 3 M15 6l3-3 M8 19l-3 2 M16 19l3 2 M7 11H3 M7 15H3 M21 11h-4 M21 15h-4',
  rodent:
    'M4 15c0-4 3-7 7-7h4a5 5 0 015 5v1a2 2 0 01-2 2h-1l-2 3h-3l-1-2H9l-2 3H4l2-3c-1-1-2-2-2-4z M16 10h.01',
  insect:
    'M12 6.5c2.5 0 4.5 2.9 4.5 6.5s-2 6.5-4.5 6.5-4.5-2.9-4.5-6.5 2-6.5 4.5-6.5z M9 7L7 4 M15 7l2-3 M8 10H3 M8 14H3 M8.5 18l-3 2 M16 10h5 M16 14h5 M15.5 18l3 2',
  bird: 'M3 14c5 0 9-4 10-8 2 4 5 6 8 6-2 5-7 9-11 9-4 0-7-3-7-7z M13 6l3-2',
  bee: 'M12 8c2.2 0 4 2.7 4 6s-1.8 6-4 6-4-2.7-4-6 1.8-6 4-6z M8.3 12h7.4 M8.3 16h7.4 M10 7L6 4 M14 7l4-3',
  spray: 'M10 9h5v12h-5z M10 9V6h5v3 M12 4h1 M19 7l-2 1 M19 11l-2-1 M20 14h-2',

  /* The activity feed, as the job card's action bar opens it on a phone. A
     speech bubble because what the column is mostly for is what people wrote. */
  activity: 'M4 5h16v11H9l-4 4V5z M8 9h8 M8 12.5h5',
  /* A text, beside Activity on the same bar, so the dots set it apart. */
  sms: 'M12 4c4.4 0 8 2.9 8 6.5S16.4 17 12 17c-1 0-2-.1-2.9-.4L5 19l1.2-3.5C4.8 14.3 4 12.5 4 10.5 4 6.9 7.6 4 12 4z M8.5 10.5h.01 M12 10.5h.01 M15.5 10.5h.01',

  /* Added for the safety and ticket blocks in a service brief. */
  shield: 'M12 3l8 3v6c0 5-3.4 8.3-8 9.5C7.4 20.3 4 17 4 12V6z',
  card: 'M3 6h18v12H3z M3 10h18 M6.5 14h4',
  mic: 'M12 4a3 3 0 0 1 3 3v4a3 3 0 0 1-6 0V7a3 3 0 0 1 3-3z M5.5 11a6.5 6.5 0 0 0 13 0 M12 17.5V21',

  /* Schedule events. A 15-minute keys stop is a 13px pill — the glyph is
     what remains when the title will not fit, and now the glyph is on every
     calendar item at every size. Decision 67. */
  task: 'M8 14a4 4 0 1 1 .9-7.9L15 12v4h-2.5v-1.5H11L8.9 12.4A4 4 0 0 1 8 14z',
  // A cup with a handle rather than the two pause bars this used to be: the
  // bars read as a grip once the glyph started appearing beside a title.
  break: 'M5 8h11v4a5.5 5.5 0 0 1-11 0z M16 9.5h1.8a2.3 2.3 0 0 1 0 4.6H16',
  unavailable: 'M12 4a8 8 0 100 16 8 8 0 000-16z M6.4 6.4l11.2 11.2',

  /* Booking time nudges. Two rails close together is shorter; far apart is
     longer. Earlier and later reuse back / forward. */
  shorter: 'M8 5v14M16 5v14M10 12h4',
  longer: 'M5 5v14M19 5v14M8 12h8',

  /* A promise the optimiser may not break. Drawn small — at 12px on the corner
     of a booking the shackle is most of what reads, so the body is kept shallow
     and the shackle tall rather than the other way round. */
  lock: 'M6 11h12v9H6z M8.5 11V7.5a3.5 3.5 0 0 1 7 0V11',
  lockOpen: 'M6 11h12v9H6z M8.5 11V7.5a3.5 3.5 0 0 1 6.8-1.2',
  /* The proposal builder's blocks and its writing tools. Decision 81. */
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  sparkles: 'M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z',
  undo: 'M9 14L4 9l5-5 M4 9h10a6 6 0 010 12h-3',
  redo: 'M15 14l5-5-5-5 M20 9H10a6 6 0 000 12h3',
  copy: 'M9 9h11v11H9z M5 15H4V4h11v1',
  text: 'M5 6h14M5 11h14M5 16h9',
  image: 'M4 5h16v14H4z M4 16l5-5 4 4 3-3 4 4 M15.5 9.5h.01',
  cover: 'M5 3h14v18H5z M8 14h8M8 17h5 M8 7h8v4H8z',
  pricing: 'M12 3v18 M16.5 7.5c0-1.7-2-3-4.5-3s-4.5 1.3-4.5 3 2 2.6 4.5 3 4.5 1.3 4.5 3-2 3-4.5 3-4.5-1.3-4.5-3',
  terms: 'M7 3h7l4 4v14H7z M14 3v4h4 M10 12h5M10 16h5',
  send: 'M4 12l16-8-6 16-2.5-6.5z M11.5 13.5L20 4',
  signOut: 'M10 4H5v16h5 M15 8l4 4-4 4 M19 12H9',

  /* The field job's action row and billing sheet, off the iOS mockup. */
  camera: 'M4 8h3l2-3h6l2 3h3v11H4z M12 16.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z',
  video: 'M3 7h12v10H3z M15 10.5l6-3.5v10l-6-3.5',
  note: 'M5 4h14v16H5z M8 9h8 M8 13h8 M8 17h5',
  stop: 'M7 7h10v10H7z',
  pause: 'M9 6v12M15 6v12',
  pencil: 'M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4',
  play: 'M8 5.5v13l10.5-6.5z',
  tap: 'M8.5 8.5a5 5 0 010 7 M12 6a8.5 8.5 0 010 12 M15.5 3.5a12 12 0 010 17',
  link: 'M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1 M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1',
  cash: 'M3 7h18v10H3z M12 14.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M6 10v.01 M18 14v.01',
  bank: 'M3 10l9-6 9 6 M5 10v8 M9.5 10v8 M14.5 10v8 M19 10v8 M3 20h18',

  /* Repeat schedules and service agreements. Decision 96. */
  repeat: 'M17 3l3 3-3 3 M4 11V9a3 3 0 013-3h13 M7 21l-3-3 3-3 M20 13v2a3 3 0 01-3 3H4',
  agreement: 'M7 3h7l4 4v14H7z M14 3v4h4 M10 12h5 M10 16l1.5 1.5L15 14',
} as const

export type IconName = keyof typeof ICON_PATHS

export function Icon({
  name,
  className,
  strokeWidth = 1.75,
}: {
  name: IconName
  className?: string
  /**
   * Raise this only below 16px, where 1.75 lands under a device pixel and the
   * glyph greys out instead of drawing. Everything at normal size leaves it be.
   */
  strokeWidth?: number
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('size-5 shrink-0', className)}
      aria-hidden="true"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  )
}
